<script lang="ts">
  import ChatDotsIcon from 'phosphor-svelte/lib/ChatDotsIcon';
  import RepeatIcon from 'phosphor-svelte/lib/RepeatIcon';
  import HeartIcon from 'phosphor-svelte/lib/HeartIcon';
  import DownloadSimpleIcon from 'phosphor-svelte/lib/DownloadSimpleIcon';

  import type { UrlPreviewView } from '#src/generated/protocol';

  import {
    parseBlueskyLink,
    fetchPostDetails,
    fetchProfile,
    type BlueskyPostDetails,
    type BlueskyProfileDetails,
    resolveMiniDoc,
    getPostRelationCount,
    type ApEmbed,
    type ImageEmbed,
    type VideoEmbed,
    parseAtUri,
  } from './bluesky';

  import LinkPreviewCard from '../LinkPreviewCard.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import { i18n } from '#lib/i18n.js';
  import { savesNatively } from '#lib/platform/files.js';
  import { downloadUrl } from '#lib/ui/download.js';
  import { mimeExtension } from '#lib/ui/mime-extension.js';
  import { formatMessageTimestamp } from '#lib/ui/date-time.js';

  interface Props {
    url: string;
    encrypted: boolean | null;
    bundled?: UrlPreviewView | null;
  }

  let { url, encrypted, bundled = null }: Props = $props();
  let record = $derived(parseBlueskyLink(url));
  let authorHandle = $state<string | null>(null);
  let details = $state<BlueskyPostDetails | null>(null);
  let profile = $state<BlueskyProfileDetails | null>(null);
  let likes = $state<number | null>(null);
  let reposts = $state<number | null>(null);
  let comments = $state<number | null>(null);
  let quote = $state<BlueskyPostDetails | null>(null);
  let quoteProfile = $state<BlueskyProfileDetails | null>(null);

  $effect(() => {
    const repo = record?.repo;
    const key = record?.key;

    authorHandle = null;
    details = null;
    profile = null;
    likes = null;
    reposts = null;
    comments = null;
    quote = null;
    quoteProfile = null;
    if (repo === undefined || key === undefined) return;

    let cancelled = false;
    void loadPost(repo, key, () => !cancelled);
    return () => {
      cancelled = true;
    };
  });

  async function loadPost(repo: string, key: string, live: () => boolean): Promise<void> {
    try {
      const miniDoc = await resolveMiniDoc(repo);
      if (!miniDoc || !live()) return;
      const { did, pds, handle } = miniDoc;
      authorHandle = handle;
      await Promise.all([
        loadDetails(pds, did, key, live),
        fetchProfile(pds, did).then((result) => {
          if (live()) profile = result;
        }),
        getPostRelationCount(did, key).then((result) => {
          if (!live()) return;
          likes = result?.likes ?? null;
          comments = result?.comments ?? null;
          reposts = result?.reposts ?? null;
        }),
      ]);
    } catch (error) {
      console.debug('[sable bluesky embed] post unavailable', error);
    }
  }

  async function loadDetails(
    pds: string,
    did: string,
    key: string,
    live: () => boolean
  ): Promise<void> {
    const result = await fetchPostDetails(pds, did, key);
    if (!live()) return;
    details = result;

    const embed = result?.value.embed;
    let quoted;
    if (embed?.$type === 'app.bsky.embed.recordWithMedia') {
      quoted = embed.record.record;
    } else if (embed?.$type === 'app.bsky.embed.record') {
      quoted = embed.record;
    }
    const doc = quoted ? parseAtUri(quoted.uri) : null;
    if (doc?.key === undefined) return;
    const quoteDoc = await resolveMiniDoc(doc.repo);
    if (!quoteDoc) return;
    await Promise.all([
      fetchPostDetails(quoteDoc.pds, quoteDoc.did, doc.key).then((next) => {
        if (live()) quote = next;
      }),
      fetchProfile(quoteDoc.pds, quoteDoc.did).then((next) => {
        if (live()) quoteProfile = next;
      }),
    ]);
  }

  const blobUrls = new WeakMap<Blob, string>();
  const createdUrls: string[] = [];

  function blobUrl(blob: Blob | undefined): string | undefined {
    if (!blob) return undefined;
    let url = blobUrls.get(blob);
    if (url === undefined) {
      url = URL.createObjectURL(blob);
      blobUrls.set(blob, url);
      createdUrls.push(url);
    }
    return url;
  }

  $effect(() => () => {
    for (const url of createdUrls) URL.revokeObjectURL(url);
  });
</script>

{#snippet embedVideo(embed: VideoEmbed)}
  {@const src = blobUrl(embed.video['moe.sable.blob'])}

  {#if embed.presentation !== 'gif'}
    <video
      controls
      controlslist={savesNatively() ? 'nodownload' : undefined}
      class="embed-solo-image"
      {src}
      title={embed.alt}
      width={embed.aspectRatio?.width}
      height={embed.aspectRatio?.height}><track kind="captions" /></video
    >
    {#if src && savesNatively()}
      <div class="embed-video-actions">
        <IconButton
          label={i18n.t('common.download')}
          size="small"
          variant="ghost"
          onclick={() =>
            void downloadUrl(src, `bluesky-video.${mimeExtension(embed.video.mimeType) ?? 'mp4'}`)}
          ><DownloadSimpleIcon /></IconButton
        >
      </div>
    {/if}
  {:else}
    <video
      autoplay
      loop
      muted
      class="embed-solo-image"
      {src}
      title={embed.alt}
      width={embed.aspectRatio?.width}
      height={embed.aspectRatio?.height}><track kind="captions" /></video
    >
  {/if}
{/snippet}

{#snippet embedImage(embed: ImageEmbed)}
  {#if embed.images.length === 1}
    {@const image = embed.images[0]}

    {@const src = blobUrl(image.image['moe.sable.blob'])}

    <img
      class="embed-solo-image"
      {src}
      alt={image.alt ?? 'No alt text...'}
      width={image.aspectRatio?.width}
      height={image.aspectRatio?.height}
    />
  {:else}
    <div class="embed-images">
      {#each embed.images as image (image.image.ref.$link)}
        {@const src = blobUrl(image.image['moe.sable.blob'])}

        {#if src}
          <!-- TODO: it would be hard because these aren't MediaImages but being able to click them to open in full view would be awesome -->
          <img
            class="embed-image"
            {src}
            alt={image.alt ?? 'No alt text...'}
            width={image.aspectRatio?.width}
            height={image.aspectRatio?.height}
          />
        {/if}
      {/each}
    </div>
  {/if}
{/snippet}
{#snippet embedQuote()}
  <div class="embed-quote-record">
    {#if quoteProfile && quote}
      {@const src = blobUrl(quoteProfile.value.avatar['moe.sable.blob'])}

      <span class="bsky-author">
        <img class="author-avatar" {src} alt="avatar" height="24" width="24" />
        <span class="displayname">{quoteProfile.value.displayName}</span>
        <span class="handle">{authorHandle}</span></span
      >
      <p class="post-content">{quote.value.text}</p>

      {#if quote.value.embed}
        {#if quote.value.embed.$type === 'app.bsky.embed.recordWithMedia'}
          {@render embed(quote.value.embed.media)}
        {:else if quote.value.embed.$type !== 'app.bsky.embed.record'}
          {@render embed(quote.value.embed)}
        {/if}
      {/if}
    {:else}
      <Spinner />
    {/if}
  </div>
{/snippet}
{#snippet embed(e: ApEmbed)}
  <!--
      TODO: support other embed types e.g
      - app.bsky.embed.gallery (see https://github.com/snarfed/bridgy-fed/issues/2504)
      - app.bsky.embed.external (link to a gif site eg klipy (oh no))
    -->
  {#if e.$type === 'app.bsky.embed.images' && e.images}
    {@render embedImage(e)}
  {:else if e.$type === 'app.bsky.embed.video'}
    {@render embedVideo(e)}
  {:else if e.$type === 'app.bsky.embed.record'}
    {@render embedQuote()}
  {:else if e.$type === 'app.bsky.embed.recordWithMedia'}
    {@render embed(e.media)}
    {@render embed(e.record)}
  {/if}
{/snippet}
{#if details}
  <div class="bsky-embed">
    <a class="bsky-header" href={url} target="_blank" rel="external noopener noreferrer">
      <span class="bsky-site">BlueSky</span>
      {#if profile}
        {@const src = blobUrl(profile.value.avatar['moe.sable.blob'])}

        <span class="bsky-author">
          <img class="author-avatar" {src} alt="avatar" height="24" width="24" />
          <span class="displayname">{profile.value.displayName}</span>
          <span class="handle">{authorHandle}</span></span
        >
      {:else}
        <Spinner />
      {/if}
    </a>
    <div class="post-body">
      <p class="post-content">{details.value.text}</p>

      {#if details.value.embed}
        {@render embed(details.value.embed)}
      {/if}
    </div>
    <div class="post-footer">
      <div class="embed-relations">
        <span class="embed-relation"><ChatDotsIcon /> {comments ?? '?'}</span>
        <span class="embed-relation"><RepeatIcon /> {reposts ?? '?'}</span>
        <span class="embed-relation"><HeartIcon /> {likes ?? '?'}</span>
      </div>
      <div class="timestamp">
        {formatMessageTimestamp(new Date(details.value.createdAt).getTime())}
      </div>
    </div>
  </div>
{:else if details === null}
  <LinkPreviewCard {url} {encrypted} {bundled} />
{/if}

<style>
  .bsky-embed {
    background: var(--surface-container);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius);
    color: var(--surface-on-container);
    display: flex;
    flex-direction: column;
    margin-top: var(--space-100);
    max-width: var(--timeline-media-max);
    overflow: hidden;
  }

  .bsky-header {
    color: inherit;
    display: flex;
    flex-direction: column;
    gap: var(--space-100);
    padding: var(--space-200) var(--space-250);
    text-decoration: none;
  }

  .bsky-site {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    text-transform: uppercase;
  }

  .bsky-author {
    display: flex;
    flex-direction: row;
    font-weight: var(--font-weight-medium);
    gap: var(--space-200);
    justify-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .bsky-header:hover .bsky-author {
    text-decoration: underline;
  }

  .author-avatar {
    border-radius: var(--radius);
  }

  .handle {
    font-weight: normal;

    &::before {
      content: '@';
    }
  }

  .post-body {
    padding-inline: var(--space-200);
  }

  .post-content {
    margin: 0;
    margin-bottom: var(--space-200);
  }

  .post-footer {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: var(--space-200) var(--space-250);
  }

  .embed-relations {
    display: flex;
    flex-direction: row;
    gap: var(--space-300);
  }

  .embed-relation {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: var(--space-100);
  }

  .embed-images {
    align-content: flex-start;
    align-items: stretch;
    display: flex;
    flex-direction: row;
    gap: var(--space-100);

    & .embed-image {
      aspect-ratio: 1 / 1;
      display: block;
      height: 100%;
      object-fit: cover;
      object-position: left 50% top 50%;
      width: 100%;
    }
  }

  .embed-solo-image {
    display: block;
    margin: 0 auto;
    max-height: 20rem;
    max-width: 100%;
    width: auto;
  }

  .embed-video-actions {
    display: flex;
    justify-content: flex-end;
  }

  .embed-quote-record {
    background: var(--surface-container);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius);
    color: var(--surface-on-container);
    display: flex;
    flex-direction: column;
    margin-top: var(--space-100);
    overflow: hidden;
    padding: var(--space-200);
    padding-bottom: 0;
  }
</style>
